// The parts of loot that need the api:
// loading the campaign's loot tables, describing the encounter and its creatures for loot_roller,
// finding the items an entry can give, putting the loot in the campaign inventory,
// and loading items for the loot table editor and its importer.

import {
  addCraftingMaterial,
  Campaign,
  COptions,
  CraftingMaterial,
  CreatureLimitation,
  Encounter,
  ItemLimitation,
  LootTable,
  Money,
  PseudoItem,
  TrueCreatureInstance,
} from "@dndModels";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from "@dndUtilities/data_utilities";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import { EncounterDifficulty } from "@dndUtilities/encounter_generator";
import { CraftingMaterialType } from "@dndModels/Types";
import {
  HarvestCreature,
  HarvestedMaterial,
  ItemGroupLike,
  LootContext,
  LootLookups,
  LootSource,
  MaterialSource,
  RolledLoot,
  totalMoney
} from "@dndUtilities/loot_roller";

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export type EncounterLootSource = LootSource<CreatureLimitation, ItemLimitation>;
export type EncounterMaterialSource = MaterialSource<CreatureLimitation>;

// The campaign's loot tables with their entries (the cached copies are light and don't have them).
export const loadLootSources = async (campaign: Campaign): Promise<{ sources: EncounterLootSource[], material_sources: EncounterMaterialSource[] }> => {
  const ids = campaign.c_options.loot_table_ids;
  if (ids.length === 0) {
    return { sources: [], material_sources: [] };
  }
  const tables = (await api.getObjects({ data_type: "loot_table" }) as LootTable[]).filter(o => ids.includes(o._id));
  for (const table of tables) {
    await api.loadObject(table);
  }
  return {
    sources: tables.map(o => ({ table_id: o._id, table_name: o.name, entries: o.entries })),
    material_sources: tables.map(o => ({ table_id: o._id, table_name: o.name, entries: o.material_entries })),
  };
};

// The creatures the party faced: creatures not on the party's team.
export const encounterCreatures = (encounter: Encounter, party_color: string): TrueCreatureInstance[] => {
  return encounter.participants
    .filter(o => o.participant_type === "Creature" && o.color !== party_color)
    .flatMap(o => o.child_participants)
    .filter((o): o is TrueCreatureInstance => o instanceof TrueCreatureInstance);
};

// Each creature the party faced, separately, for harvesting crafting materials.
export const harvestCreatures = (creatures: TrueCreatureInstance[]): HarvestCreature<CreatureLimitation>[] => {
  return creatures.map(o => ({
    key: o.true_id,
    creature_id: o.creature_id,
    name: o.name,
    creature_type: o.creature_type,
    cr: o.challenge_rating,
    matches: (limit: CreatureLimitation) => limit.isEmpty() || data_util.fitCreatureLimit(o, limit),
  }));
};

export const buildLootContext = (
  encounter: Encounter,
  creatures: TrueCreatureInstance[],
  difficulty: EncounterDifficulty,
  known_environment_ids: string[]
): LootContext<CreatureLimitation> => {
  return {
    difficulty,
    environment_ids: encounter.environment_ids,
    known_environment_ids,
    max_cr: Math.max(0, ...creatures.map(o => o.challenge_rating)),
    creatureMatches: limit => limit.isEmpty() || creatures.some(o => data_util.fitCreatureLimit(o, limit)),
  };
};

const fitsValue = (item: PseudoItem, group: ItemGroupLike<ItemLimitation>) =>
  (group.min_value < 0 || item.cost >= group.min_value) &&
  (group.max_value < 0 || item.cost <= group.max_value);

// Finding the items for a limitation is slow, so each one is only done once.
// data_util has to be loaded first (makeLootLookups and loadEditorItems do it).
const itemsForGroups = () => {
  const cache = new Map<string, { id: string, name: string }[]>();
  return (group: ItemGroupLike<ItemLimitation>) => {
    if (group.source === "Specific Item") {
      const item = group.item_id === "" ? undefined : char_util.getPseudoItem(group.item_id);
      return item && item.base_item ? [{ id: item.id, name: item.name }] : [];
    }
    if (group.item_limit.isEmpty()) {
      return [];
    }
    const key = `${JSON.stringify(group.item_limit.toDBObj())}|${group.min_value}|${group.max_value}`;
    let items = cache.get(key);
    if (!items) {
      items = char_util.getPseudoItems({ limitation: group.item_limit, no_extras: true })
        .filter(o => fitsValue(o, group))
        .map(o => ({ id: o.id, name: o.name }));
      cache.set(key, items);
    }
    return items;
  };
};

export const makeLootLookups = async (campaign: Campaign): Promise<LootLookups<ItemLimitation>> => {
  if (data_util.items.length === 0) {
    await data_util.loadData({ char: campaign });
  }
  await data_util.core_data_loading;
  return {
    itemsFor: itemsForGroups(),
  };
};

// Loot tables aren't tied to a campaign, so the editor loads items with the user's own source books.
let editor_source: Campaign | null = null;

export const loadEditorItems = async () => {
  if (!editor_source) {
    editor_source = new Campaign();
    editor_source._id = "loot_table_editor";
  }
  const options = new COptions();
  options.source_books = api.user ? [...api.user.source_book_ids] : [];
  editor_source.old_c_options = options;
  await data_util.loadData({ char: editor_source });
  await data_util.core_data_loading;
};

// Every item by name, including magic items and their versions (like each Spell Scroll), for matching pasted tables.
export const allNamedItems = (): { id: string, name: string }[] => {
  return data_util.base_pseudo_items
    .flatMap(o => char_util.getPseudoItemsForItem({ pseudo_item: o }))
    .filter(o => o.base_item)
    .map(o => ({ id: o.id, name: o.name }));
};

export const pseudoItemName = (id: string) => {
  const item = id === "" ? undefined : char_util.getPseudoItem(id);
  return item && item.base_item ? item.name : "";
};

export const toCraftingMaterial = (harvested: HarvestedMaterial) => new CraftingMaterial({
  material_type: harvested.material_type as CraftingMaterialType,
  amount: harvested.amount,
  weight: harvested.weight,
  value: harvested.value,
  source_creature_id: harvested.creature_id,
  source_name: harvested.creature_name,
  source_creature_type: harvested.creature_type,
  source_cr: harvested.creature_cr,
});

// Puts the money, items, and crafting materials in the campaign inventory, the same way quest rewards are granted.
export const grantLootToCampaign = async (campaign: Campaign, results: RolledLoot[], materials: HarvestedMaterial[] = []) => {
  // Get the latest campaign inventory so we don't overwrite changes from elsewhere.
  await api.loadCampaignInventory({ campaign });
  if (data_util.items.length === 0) {
    await data_util.loadData({ char: campaign });
  }
  await data_util.core_data_loading;
  await connect_util.connectItems(campaign);

  const money = totalMoney(results);
  campaign.inventory.money.add(new Money({
    platinum: money.pp,
    gold: money.gp,
    electrum: money.ep,
    silver: money.sp,
    copper: money.cp
  }));

  for (const item of results.flatMap(o => o.items)) {
    const pseudo_item = char_util.getPseudoItem(item.id);
    if (!pseudo_item || !pseudo_item.base_item) {
      console.error(`Loot item not found: ${item.id}`);
      continue;
    }
    // addItem only makes one of an item that doesn't stack, so add those one at a time.
    const probe = await connect_util.createCharItem({ char: campaign, item: pseudo_item });
    if (probe.stackable) {
      await connect_util.addItem({ char: campaign, item: pseudo_item, count: item.count });
    } else {
      for (let i = 0; i < item.count; ++i) {
        await connect_util.addItem({ char: campaign, item: pseudo_item });
      }
    }
  }

  for (const harvested of materials) {
    addCraftingMaterial(campaign.inventory.crafting_materials, toCraftingMaterial(harvested));
  }

  await api.upsertObject("campaign_inventory", campaign.inventory);
  await connect_util.refreshCampaignInventory(campaign, campaign._id);
  campaign.subscription_holder.itemsChanged();
};
